import { Component, computed, inject, signal } from '@angular/core';
import { createForm } from '@sonofabit/ng-core';
import { currentTenant } from '@/utils/admin-utils';
import {
  TenantItemDto,
  EquipmentItemDto,
  BuildingEquipmentService,
  BuildingEquipmentStatisticDto
} from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';
import { NbDialogService } from '@nebular/theme';
import {
  TgaStatisticDialogComponent
} from '@pages/tga-statistic-page/tga-statistic-dialog/tga-statistic-dialog.component';
import { Router } from '@angular/router';

@Component({
  selector: 'wr-tga-statistic-page',
  standalone: false,
  templateUrl: './tga-statistic-page.component.html',
  styleUrl: './tga-statistic-page.component.scss'
})
export class TgaStatisticPageComponent {
  private readonly buildingEquipmentService = inject(BuildingEquipmentService);
  private readonly dialogService = inject(NbDialogService);
  private readonly router = inject(Router);

  currentTenant = currentTenant();

  filterForm = signal(this.createForm());

  searchText = signal<string>('');

  readonly loading = loader();

  buildingEquipments = asyncComputed(() => {
    this.reload();
    const externalTenantId = this.filterForm().controls.externalTenantId.value;
    const internalTenantId = this.filterForm().controls.internalTenantId.value;
    const equipmentId = this.filterForm().controls.equipment.value?.id;

    if(externalTenantId === undefined || externalTenantId === null) return [];

    return this.buildingEquipmentService.getBuildingEquipmentStatistic({
      equipmentId: equipmentId,
      externalTenantId: externalTenantId,
      internalTenantId: internalTenantId
    });
  }, [], this.loading);

  filteredBuildingEquipments = computed(() => {
    const searchText = this.searchText().toLowerCase();
    const buildingEquipments = this.buildingEquipments() ?? [];

    if (!searchText) return buildingEquipments;

    return buildingEquipments.filter(buildingEquipment =>
      buildingEquipment.name?.toLowerCase().includes(searchText)
    );
  });

  reload = signal<number>(0);

  private createForm() {
    const tenant = this.currentTenant();
    let externalTenantInitialValue: TenantItemDto | undefined;

    if(tenant && tenant.type == 'External') {
      externalTenantInitialValue = tenant;
    }

    const form = createForm(({ field }) =>{
      return {
        externalTenantId: field({
          value: externalTenantInitialValue?.id
        }),
        internalTenantId: field({
          value: undefined as string | undefined
        }),
        equipment: field({
          value: undefined as EquipmentItemDto | undefined,
          toDto: value => value?.id
        })
      };
    });

    form.valueChanges.subscribe(() => {
      this.reload.set(this.reload()+1);
    });

    return form;
  }

  searchTextChange($event: any) {
    this.searchText.set($event);
  }

  getIcon(statisticItem: BuildingEquipmentStatisticDto): string {
    return statisticItem.icon?.presignedUrl ?? '';
  }

  openBuildingDialog($event: any) {
    this.dialogService.open(TgaStatisticDialogComponent, {
      context: {
        title: 'TGA Auflistung',
        externalTenantId: this.filterForm().value.externalTenantId,
        equipmentId: $event.equipmentId,
      }
    });
  }

  navigateToBuildingEquipments(event: Event, equipmentId: string): void {
    event.stopPropagation();
    const externalTenantId = this.filterForm().controls.externalTenantId.value;
    if (this.currentTenant()?.type === 'Internal') {
      this.router.navigate(['/buildingEquipments'], { queryParams: { equipmentId: equipmentId,
        externalTenantId: externalTenantId } });
    }
    else if (this.currentTenant()?.type === 'External') {
      this.router.navigate(['/external/buildingEquipments'], { queryParams: { equipmentId: equipmentId } });
    }
  }
}
